import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';

const DailyScriptureBulkUpload = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    setFiles(list);
    setResult(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) {
      alert('파일을 선택하세요.');
      return;
    }
    setUploading(true);
    setResult(null);

    let totalScriptures = 0;
    let totalVerses = 0;
    const errors = [];

    try {
      for (const file of files) {
        let jsonData;
        try {
          const text = await file.text();
          jsonData = JSON.parse(text);
        } catch (err) {
          errors.push(`[${file.name}] JSON 파싱 오류: 파일이 올바른 JSON 형식인지 확인하세요.`);
          continue;
        }

        if (!Array.isArray(jsonData)) {
          errors.push(`[${file.name}] JSON 루트는 배열이어야 합니다.`);
          continue;
        }

        for (const scripture of jsonData) {
          try {
            const toNullIfEmpty = (value) => {
              if (value === null || value === undefined) return null;
              if (typeof value === 'string' && value.trim() === '') return null;
              return value;
            };

            const { data: scriptureData, error: scriptureError } = await supabase
              .from('daily_scriptures')
              .insert({
                date: scripture.date,
                title: scripture.title,
                reference: scripture.reference,
                commentary: toNullIfEmpty(scripture.commentary),
                question: toNullIfEmpty(scripture.question),
                dictionary: toNullIfEmpty(scripture.dictionary),
                hymn_number:
                  scripture.hymn_number !== null && scripture.hymn_number !== undefined
                    ? Number(scripture.hymn_number)
                    : null,
                is_active: scripture.is_active || 'N',
                country_cd: scripture.country_cd || 'KR',
                group_cd: scripture.group_cd || '100',
                qt_ymd: scripture.qt_ymd,
                language: scripture.language,
                daily_insight: toNullIfEmpty(scripture.daily_insight),
              })
              .select();

            if (scriptureError) {
              const errorMsg = `[${file.name}][${scripture.date || scripture.title || '알 수 없음'}] Daily scripture 삽입 오류: ${scriptureError.message}`;
              console.error(errorMsg);
              errors.push(errorMsg);
              continue;
            }

            const dailyScriptureId = scriptureData[0].id;
            totalScriptures++;

            if (scripture.scripture_verses && Array.isArray(scripture.scripture_verses)) {
              for (const verseGroup of scripture.scripture_verses) {
                if (verseGroup.verse_arr && Array.isArray(verseGroup.verse_arr)) {
                  const versesToInsert = verseGroup.verse_arr.map((verse) => ({
                    daily_scripture_id: dailyScriptureId,
                    verse_number: verse.verse_number,
                    verse_text: verse.verse_text,
                    order_index: verse.order_index,
                    book_name: verseGroup.book_name,
                    chapter_number: verseGroup.chapter_number,
                  }));

                  const { error: versesError } = await supabase.from('scripture_verses').insert(versesToInsert);

                  if (versesError) {
                    const errorMsg = `[${file.name}][${scripture.date || scripture.title || '알 수 없음'}] Scripture verses 삽입 오류: ${versesError.message}`;
                    console.error(errorMsg);
                    errors.push(errorMsg);
                  } else {
                    totalVerses += versesToInsert.length;
                  }
                }
              }
            }
          } catch (err) {
            const errorMsg = `[${file.name}][${scripture?.date || scripture?.title || '알 수 없음'}] 처리 오류: ${err.message}`;
            console.error(errorMsg);
            errors.push(errorMsg);
          }
        }
      }

      const filesSummary =
        files.length > 1 ? `\n처리한 파일: ${files.length}개 (${files.map((f) => f.name).join(', ')})` : '';

      let message = '';
      if (errors.length > 0) {
        message = `업로드 완료: Daily Scriptures ${totalScriptures}건, Scripture Verses ${totalVerses}건${filesSummary}\n\n오류 발생: ${errors.length}건\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... 외 ${errors.length - 5}건` : ''}`;
      } else {
        message = `성공적으로 업로드되었습니다. Daily Scriptures: ${totalScriptures}건, Scripture Verses: ${totalVerses}건${filesSummary}`;
      }

      setResult({
        success: errors.length === 0,
        message,
      });
    } catch (err) {
      setResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3">
        <h6 className="m-0 font-weight-bold text-primary">큐티 본문 일괄 업로드</h6>
      </div>
      <div className="card-body">
        <form onSubmit={handleUpload}>
          <div className="form-group">
            <label>JSON 텍스트 파일 선택 (여러 개 선택 가능)</label>
            <input
              type="file"
              className="form-control"
              accept=".json,.txt,application/json,text/plain"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
            {files.length > 0 && (
              <small className="form-text text-muted d-block mt-1">
                선택됨: {files.length}개 — {files.map((f) => f.name).join(', ')}
              </small>
            )}
          </div>
          <button type="submit" className="btn btn-primary" disabled={uploading || files.length === 0}>
            {uploading ? '업로드 중...' : '업로드'}
          </button>
        </form>
        {result && (
          <div className={`mt-3 alert ${result.success ? 'alert-success' : 'alert-danger'}`} style={{ whiteSpace: 'pre-wrap' }}>
            {result.message}
          </div>
        )}
        <div className="mt-3">
          <small>
            <b>※ 파일 형식</b>
            <br />
            각 파일은 JSON 배열 형태여야 하며, 각 항목은 다음 구조를 가져야 합니다:
            <br />
            {'{'}"date":"2024-09-01","title":"제목","reference":"구절","commentary":"해설","scripture_verses":[...]{'}'}
            <br />
            scripture_verses 배열의 각 항목은 verse_arr 배열을 포함해야 합니다.
          </small>
        </div>
      </div>
    </div>
  );
};

export default DailyScriptureBulkUpload;
